/**
 * ImportsPage — dashboard of past import batches.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileUp, FileText, Loader2, ScanLine, Upload } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import importService from '../services/importService';
import { ImportBatch } from '../types/importBatch';

const formatLabel = (s?: string): string =>
  (s || '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusBadgeClasses = (status: string): string => {
  switch (status) {
    case 'complete':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'importing':
    case 'queuing_research':
    case 'parsing':
      return 'bg-blue-100 text-blue-800';
    case 'preview':
    case 'mapping':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const ImportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await importService.listBatches({ page: 1, size: 50 });
      if (res.error) {
        toast.error(res.error);
        setBatches([]);
      } else {
        setBatches(res.data?.batches || []);
      }
      setLoading(false);
    };
    load();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Imports</h1>
            <p className="text-gray-600 mt-1">
              Bring investors into the library from spreadsheets, vCards,
              LinkedIn exports, business cards, or company pitch decks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/imports/new/business-cards"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <ScanLine className="w-4 h-4 mr-2" />
              Business Cards
            </Link>
            <Link
              to="/companies/upload-brochure"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              title="Upload a company pitch deck — parses, scores, and lets you generate a standardized investor-ready version"
            >
              <FileText className="w-4 h-4 mr-2" />
              Pitch Deck
            </Link>
            <Link
              to="/imports/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <FileUp className="w-4 h-4 mr-2" />
              New Import
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" /> Loading imports...
          </div>
        ) : batches.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <Upload className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              No imports yet
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload your first CSV, LinkedIn export, or batch of business cards.
            </p>
            <Link
              to="/imports/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <FileUp className="w-4 h-4 mr-2" />
              Start an import
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    File
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Records
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Research
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {batches.map((b) => (
                  <tr
                    key={b.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/imports/${b.id}`)}
                  >
                    <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-xs">
                      {b.source_file || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatLabel(b.source_type)}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded ${statusBadgeClasses(
                          b.status
                        )}`}
                      >
                        {formatLabel(b.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {b.imported_records} / {b.total_records}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {b.research_complete} / {b.research_queued}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {b.created_at
                        ? new Date(b.created_at).toLocaleString()
                        : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ImportsPage;
